/**
 * HRIS Jobs list page.
 *
 * Displays all job postings in a table with:
 *  - Titre / Département / Statut chip / Visible inline Switch / Date de
 *    publication / "Dans le feed" chip (client-computed: status=open && visible)
 *  - Links to "page publique" (job.atek.io/<lang>/careers/<slug>) and "feed"
 *    (/indeed.xml).
 *  - "Nouveau poste +" button → /jobs/new (editor with no :id).
 *
 * Working language: French. All labels in French.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Plus, ExternalLink, Rss } from 'lucide-react';
import { PageShell } from '@/shared/components';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { useJobPostings, useUpdateJobPosting } from '../api/jobsApi';
import type { JobPosting, JobStatus } from '../api/jobsApi';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PUBLIC_BASE_URL = 'https://job.atek.io';
const FEED_URL = `${PUBLIC_BASE_URL}/indeed.xml`;

// ---------------------------------------------------------------------------
// Status chip
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<JobStatus, string> = {
  draft: 'Brouillon',
  open: 'Ouvert',
  closed: 'Fermé',
};

const STATUS_CLASSES: Record<JobStatus, string> = {
  draft: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  open: 'bg-green-100 text-green-800 border-green-200',
  closed: 'bg-gray-100 text-gray-700 border-gray-200',
};

function StatusChip({ status }: { status: JobStatus }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${STATUS_CLASSES[status]}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

// ---------------------------------------------------------------------------
// "Dans le feed" chip — computed client-side from status+visible.
// We cannot query the real Indeed index state; honest chip per spec §12.5.
// ---------------------------------------------------------------------------

const FEED_TOOLTIP =
  "Indeed explore le flux ~4×/jour. Il peut s'écouler quelques heures avant qu'un poste apparaisse ou disparaisse. Nous ne pouvons pas interroger l'état réel d'indexation d'Indeed.";

function FeedChip({ inFeed }: { inFeed: boolean }) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium cursor-default ${
              inFeed
                ? 'bg-blue-100 text-blue-800 border-blue-200'
                : 'bg-gray-100 text-gray-500 border-gray-200'
            }`}
          >
            {inFeed ? 'Dans le feed : Oui' : 'Dans le feed : Non'}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-center">
          {FEED_TOOLTIP}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ---------------------------------------------------------------------------
// Visible Switch — inline PATCH, optimistic UI
// ---------------------------------------------------------------------------

function VisibleSwitch({ job }: { job: JobPosting }) {
  const [localVisible, setLocalVisible] = useState(job.visible);
  const updateJob = useUpdateJobPosting();

  const handleToggle = (checked: boolean) => {
    setLocalVisible(checked);
    updateJob.mutate(
      { id: job.id, data: { visible: checked } },
      {
        onError: () => {
          // Rollback optimistic update on failure
          setLocalVisible(!checked);
        },
      }
    );
  };

  return (
    <Switch
      checked={localVisible}
      onCheckedChange={handleToggle}
      disabled={updateJob.isPending}
      aria-label={`Visibilité du poste ${job.titleFr}`}
    />
  );
}

// ---------------------------------------------------------------------------
// Table skeleton
// ---------------------------------------------------------------------------

function TableSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <TableRow key={i}>
          {Array.from({ length: 6 }).map((__, j) => (
            <TableCell key={j}>
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function HrisJobsPage() {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useJobPostings();

  const jobs = data?.jobPostings ?? [];

  return (
    <PageShell
      actions={
        <Button
          size="sm"
          onClick={() => navigate('/jobs/new')}
          className="gap-1"
        >
          <Plus className="h-4 w-4" />
          Nouveau poste +
        </Button>
      }
    >
      <div className="overflow-auto">
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Titre</TableHead>
                <TableHead>Département</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Visible</TableHead>
                <TableHead>Date de publication</TableHead>
                <TableHead>Feed</TableHead>
                <TableHead className="text-right">Liens</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableSkeleton rows={5} />
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-destructive">
                    Erreur lors du chargement des postes.
                  </TableCell>
                </TableRow>
              ) : jobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                    Aucun poste trouvé. Créez votre premier poste.
                  </TableCell>
                </TableRow>
              ) : (
                jobs.map((job) => {
                  const inFeed = job.status === 'open' && job.visible;
                  const datePosted = job.datePosted
                    ? format(new Date(job.datePosted), 'dd MMM yyyy')
                    : '—';

                  return (
                    <TableRow
                      key={job.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/jobs/${job.id}/edit`)}
                    >
                      <TableCell>
                        <div className="font-medium">{job.titleFr}</div>
                        <div className="text-xs text-muted-foreground">{job.titleEn}</div>
                      </TableCell>
                      <TableCell>
                        {job.departmentFr ? (
                          <span className="text-sm">{job.departmentFr}</span>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusChip status={job.status} />
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <VisibleSwitch job={job} />
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">{datePosted}</span>
                      </TableCell>
                      <TableCell>
                        <FeedChip inFeed={inFeed} />
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-2">
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <a
                                  href={`${PUBLIC_BASE_URL}/fr/careers/${job.slug}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                                  aria-label={`Page publique de ${job.titleFr}`}
                                >
                                  <ExternalLink className="h-3 w-3" />
                                  page publique
                                </a>
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                job.atek.io/fr/careers/{job.slug}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <a
                                  href={FEED_URL}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-orange-600 hover:underline"
                                  aria-label="Indeed XML feed"
                                >
                                  <Rss className="h-3 w-3" />
                                  feed
                                </a>
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                {FEED_URL}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </PageShell>
  );
}
